import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { Flame, Home, CheckSquare, ClipboardCheck, Users } from "lucide-react";

const DRILL_TYPES = [
  { v: "fire", l: "Fire" },
  { v: "earthquake", l: "Earthquake" },
  { v: "tornado", l: "Tornado / Severe Weather" },
  { v: "lockdown", l: "Lockdown" },
  { v: "general", l: "General Evacuation" },
];

const DRILL_CLASSIFICATIONS = [
  { v: "planned", l: "Planned" },
  { v: "unplanned", l: "Unplanned" },
  { v: "daytime", l: "Daytime" },
  { v: "nighttime", l: "Nighttime" },
];

export default function EvacuationDrillForm() {
  const navigate = useNavigate();
  const [eligible, setEligible] = useState({ contractor_id: "", contractor_name: "", clients: [] });
  const [selected, setSelected] = useState(new Set());
  const [drillDate, setDrillDate] = useState(new Date().toISOString().slice(0, 10));
  const [drillTime, setDrillTime] = useState("");
  const [drillType, setDrillType] = useState("fire");
  const [classification, setClassification] = useState("planned");
  const [seconds, setSeconds] = useState("");
  const [participants, setParticipants] = useState("");
  const [escapeRoute, setEscapeRoute] = useState("");
  const [effectiveness, setEffectiveness] = useState("");
  const [corrective, setCorrective] = useState("");
  const [notes, setNotes] = useState("");
  const [sig, setSig] = useState({ signature_type: "typed", signature_data: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/evacuation-drills/eligible-clients").then(({ data }) => {
      setEligible(data);
      setSelected(new Set(data.clients.map((c) => c.id)));
      // Pre-fill participant count to the seed roster size
      if (data.clients.length) setParticipants(String(data.clients.length));
    });
  }, []);

  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  const submit = async () => {
    if (!eligible.contractor_id) return toast.error("No contractor profile is assigned to your account");
    if (selected.size === 0) return toast.error("Check at least one client who participated");
    if (!drillDate) return toast.error("Drill date required");
    if (!escapeRoute.trim()) return toast.error("Escape route is required for DSPD audit");
    if (!effectiveness.trim()) return toast.error("Document effectiveness / problems encountered");
    if (!corrective.trim()) return toast.error("Document corrective actions planned / taken");
    if (!sig.signature_data) return toast.error("Electronic signature attestation required");
    setBusy(true);
    try {
      await api.post("/evacuation-drills", {
        contractor_id: eligible.contractor_id,
        drill_date: drillDate,
        drill_time: drillTime,
        drill_type: drillType,
        drill_classification: classification,
        evacuation_time_seconds: seconds ? parseInt(seconds, 10) : null,
        participant_count: participants ? parseInt(participants, 10) : null,
        escape_route: escapeRoute,
        effectiveness_problems: effectiveness,
        corrective_actions: corrective,
        obstacles_encountered: effectiveness, // backwards-compat mirror
        notes,
        client_ids: Array.from(selected),
        signature_data: sig.signature_data,
      });
      toast.success(`Drill logged. ${selected.size} client${selected.size > 1 ? "s" : ""} marked complete.`);
      navigate("/provider");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="evacuation-drill-form">
      <div>
        <p className="text-label">Quarterly DSPD Compliance</p>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          <Flame className="w-6 h-6 text-red-600" /> Log Evacuation Drill
        </h1>
        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
          <Home className="w-3.5 h-3.5" /> {eligible.contractor_name || "—"}
        </p>
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Drill Date</Label>
            <Input type="date" value={drillDate} onChange={(e) => setDrillDate(e.target.value)} data-testid="drill-date" />
          </div>
          <div>
            <Label>Time of Drill</Label>
            <Input type="time" value={drillTime} onChange={(e) => setDrillTime(e.target.value)} data-testid="drill-time" placeholder="HH:MM" />
          </div>
          <div>
            <Label>Classification</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger data-testid="drill-classification"><SelectValue /></SelectTrigger>
              <SelectContent>{DRILL_CLASSIFICATIONS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Hazard Type</Label>
            <Select value={drillType} onValueChange={setDrillType}>
              <SelectTrigger data-testid="drill-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DRILL_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Total Evacuation Time (seconds)</Label>
            <Input type="number" min="0" value={seconds} onChange={(e) => setSeconds(e.target.value)}
                   placeholder="e.g. 95" data-testid="drill-seconds" />
          </div>
          <div>
            <Label>Individuals Participating</Label>
            <Input type="number" min="1" value={participants} onChange={(e) => setParticipants(e.target.value)}
                   placeholder="Count" data-testid="drill-participants" />
          </div>
        </div>
        <div>
          <Label>Escape Route / Exit Used</Label>
          <Input value={escapeRoute} onChange={(e) => setEscapeRoute(e.target.value)}
                 placeholder="e.g., Front door → south sidewalk muster point" data-testid="drill-escape-route" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Effectiveness / Problems Encountered <span className="text-red-600">*</span></Label>
            <Textarea value={effectiveness} onChange={(e) => setEffectiveness(e.target.value)}
                      placeholder="What worked, what didn't. Required for DSPD audit."
                      rows={3} data-testid="drill-effectiveness" />
          </div>
          <div>
            <Label>Corrective Actions Planned / Taken <span className="text-red-600">*</span></Label>
            <Textarea value={corrective} onChange={(e) => setCorrective(e.target.value)}
                      placeholder="Concrete next-time improvements."
                      rows={3} data-testid="drill-corrective" />
          </div>
        </div>
        <div>
          <Label>Additional Notes (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything else the agency should know..."
                    rows={2} data-testid="drill-notes" />
        </div>
      </Card>

      <Card className="p-4 space-y-3" data-testid="drill-client-list">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-primary" />
          <p className="text-label">Clients who participated</p>
        </div>
        <p className="text-xs text-muted-foreground">
          All active clients in this home are pre-checked. Uncheck anyone who was absent.
          Submitting will mark the drill complete for every checked client across the dashboard
          AND unlock standard daily logging if you were in a Quarterly Drill Lockout state.
        </p>
        <div className="space-y-1.5">
          {eligible.clients.map((c) => (
            <label key={c.id}
                   className={`flex items-center gap-3 p-3 rounded-md border-2 cursor-pointer transition-colors ${
                     selected.has(c.id) ? "border-primary bg-primary/5" : "border-border"
                   }`}>
              <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)}
                        className="h-5 w-5"
                        data-testid={`drill-client-${c.id}`} />
              <span className="font-medium">{c.name}</span>
            </label>
          ))}
        </div>
      </Card>

      <Card className="p-4 space-y-2">
        <p className="text-label flex items-center gap-1.5"><ClipboardCheck className="w-4 h-4 text-primary" /> Contractor Electronic Signature Attestation</p>
        <SignaturePad onChange={setSig} />
      </Card>

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={() => navigate(-1)} data-testid="drill-cancel">Cancel</Button>
        <Button className="flex-1" onClick={submit} disabled={busy} data-testid="drill-submit">
          {busy ? "Submitting..." : "Submit drill"}
        </Button>
      </div>
    </div>
  );
}
